import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateRangeField } from './DateRangeField';
import { CheckRow } from './ui/CheckRow';
import { DenseList, DenseRow } from './ui/DenseList';
import { Button } from './ui/primitives';
import { useShellTier } from '../layouts/shellTier';
import { getErrorMessage } from '../api/client';
import {
  getAttentionInbox,
  getQuestionAnalytics,
  getSupervisionStores,
  getSupervisionSummary,
  getSupervisionTemplates,
  getSupervisionTrends,
  getTemplateQuestions,
  getTextResponses,
  reviewAttention,
} from '../api/formSupervision';
import { QUESTION_TYPE_LABELS } from '../types/forms';
import type {
  AttentionItem,
  QuestionAnalytics,
  ReviewStatus,
  SubmissionContext,
  SupervisionFilters,
  SupervisionStoreRow,
  SupervisionSummary,
  SupervisionTemplateRow,
  TemplateQuestionOption,
  TextItem,
  TrendBucket,
  TrendPoint,
} from '../types/formSupervision';
import { SUPERVISION_MAX_RANGE_DAYS } from '../types/formSupervision';
import type { Store } from '../types/stores';
import { DEFAULT_STORE_TIMEZONE, dateToStoreISO } from '../utils/timezones';

// Fase 23C: panel de supervision, excepciones y analitica operacional (SOLO LECTURA + acuse). Convierte
// las respuestas de formularios en herramienta de supervision para OWNER/MANAGER. El backend valida
// permiso/alcance; aqui solo se presenta. No inventa "formularios esperados": los borradores son reales.

// --- helpers de fecha (local YYYY-MM-DD) ---
const toISODate = (d: Date) => dateToStoreISO(d);
function defaultRange(): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - 29); // ~30 dias por defecto
  return { from: toISODate(from), to: toISODate(to) };
}
// Zona horaria de la TIENDA (patrón 17A-4), no la del dispositivo (con el teléfono en otra zona
// las horas se veían corridas vs. el historial de cortes).
const fmtDateTime = (iso: string | null, timeZone: string) => (iso ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone }).format(new Date(iso)) : '—');
const fmtDate = (iso: string | null, timeZone: string = DEFAULT_STORE_TIMEZONE) => (iso ? new Date(iso).toLocaleDateString('es-MX', { dateStyle: 'medium', timeZone }) : '—');
const contextLabel = (c: SubmissionContext) => (c === 'SHIFT_CLOSE' ? 'Corte de turno' : 'Manual');
const pct = (n: number, d: number) => (d > 0 ? `${Math.round((n / d) * 1000) / 10}%` : '—');

export function FormSupervisionPanel({ stores, canReview }: { stores: Store[]; canReview: boolean }) {
  const tzFor = (id: string) => stores.find((store) => store.id === id)?.timezone ?? DEFAULT_STORE_TIMEZONE;
  const init = useMemo(defaultRange, []);
  const [from, setFrom] = useState(init.from);
  const [to, setTo] = useState(init.to);
  const [storeId, setStoreId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [context, setContext] = useState<'' | SubmissionContext>('');
  const [includeVoided, setIncludeVoided] = useState(false);

  // Los filtros "aplicados" (se congelan al pulsar Aplicar, para no recargar en cada tecla).
  const [applied, setApplied] = useState<SupervisionFilters>({ from: init.from, to: init.to });
  const [rangeError, setRangeError] = useState<string>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);

  const [summary, setSummary] = useState<SupervisionSummary | null>(null);
  const [storeRows, setStoreRows] = useState<SupervisionStoreRow[]>([]);
  const [templateRows, setTemplateRows] = useState<SupervisionTemplateRow[]>([]);

  const apply = () => {
    const f = new Date(`${from}T00:00:00Z`).getTime();
    const t = new Date(`${to}T00:00:00Z`).getTime();
    if (Number.isNaN(f) || Number.isNaN(t) || f > t) { setRangeError('Rango de fechas invalido.'); return; }
    if ((t - f) / 86_400_000 > SUPERVISION_MAX_RANGE_DAYS) { setRangeError(`El rango no puede exceder ${SUPERVISION_MAX_RANGE_DAYS} dias.`); return; }
    setRangeError(undefined);
    setApplied({ from, to, storeId: storeId || undefined, templateId: templateId || undefined, context: context || undefined, includeVoided: includeVoided || undefined });
  };

  const load = useCallback(async () => {
    setLoading(true); setError(undefined);
    try {
      const [s, st, tp] = await Promise.all([
        getSupervisionSummary(applied),
        getSupervisionStores(applied),
        getSupervisionTemplates(applied),
      ]);
      setSummary(s); setStoreRows(st.stores); setTemplateRows(tp.templates);
    } catch (e) { setError(getErrorMessage(e, 'No fue posible cargar la supervision.')); }
    finally { setLoading(false); }
  }, [applied]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="form-supervision">
      <p className="section-hint">Una regla de atención marca sola las respuestas que requieren revisión (ej. un valor fuera de rango o un "No" en limpieza). Aquí las revisas y las das por atendidas.</p>
      <fieldset className="form-supervision__filters">
        <legend>Filtros</legend>
        <DateRangeField allowClear={false} from={from} to={to} onChange={(nextFrom, nextTo) => { setFrom(nextFrom); setTo(nextTo); }} />
        <label className="field"><span>Tienda</span>
          <select value={storeId} onChange={(e) => setStoreId(e.target.value)}>
            <option value="">Todas mis tiendas</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label className="field"><span>Formulario</span>
          <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">Todos</option>
            {templateRows.map((t) => <option key={t.templateId} value={t.templateId}>{t.name ?? '(sin nombre)'}</option>)}
          </select>
        </label>
        <label className="field"><span>Origen</span>
          <select value={context} onChange={(e) => setContext(e.target.value as '' | SubmissionContext)}>
            <option value="">Todos</option>
            <option value="MANUAL">Manual</option>
            <option value="SHIFT_CLOSE">Corte de turno</option>
          </select>
        </label>
        <CheckRow checked={includeVoided} label="Incluir cortes anulados" onChange={setIncludeVoided} plain />
        <button className="button button--primary" onClick={apply} type="button">Aplicar</button>
        {rangeError && <span className="form-supervision__rangeerr" role="alert">{rangeError}</span>}
      </fieldset>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      {loading ? <div className="inline-loading"><span className="spinner" />Cargando supervision…</div> : (
        <>
          {summary && <SummaryCards s={summary} />}

          <AttentionInbox filters={applied} canReview={canReview} tzFor={tzFor} onReviewed={() => void load()} />

          <section className="form-supervision__section" aria-label="Actividad y hallazgos por tienda">
            <h3>Por tienda</h3>
            {storeRows.length === 0 ? <p className="form-supervision__empty">Sin actividad en el periodo.</p> : (
              <div className="data-table-wrap">
                {/* P-076: tabla analítica compacta (se comparan cifras): identidad fija y desplazamiento local en el teléfono. */}
                <table className="data-table n3-table--analytic">
                  <thead><tr><th>Tienda</th><th>Enviados</th><th>Con atención</th><th>Hallazgos</th><th>Pendientes</th></tr></thead>
                  <tbody>
                    {storeRows.map((r) => (
                      <tr key={r.storeId}>
                        <td>{r.storeName}</td><td>{r.submitted}</td><td>{r.submissionsWithAttention}</td><td>{r.attentionHits}</td>
                        <td>{r.pendingReview > 0 ? <span className="form-badge form-badge--attention">{r.pendingReview}</span> : '0'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="form-supervision__section" aria-label="Actividad por formulario">
            <h3>Por formulario</h3>
            {templateRows.length === 0 ? <p className="form-supervision__empty">Sin actividad en el periodo.</p> : (
              <div className="data-table-wrap">
                <table className="data-table n3-table--analytic">
                  <thead><tr><th>Formulario</th><th>Enviados</th><th>Tiendas</th><th>Versiones</th><th>Último</th><th>Con atención</th><th>Hallazgos</th></tr></thead>
                  <tbody>
                    {templateRows.map((r) => (
                      <tr key={r.templateId}>
                        <td>{r.name ?? '(sin nombre)'}</td><td>{r.submitted}</td><td>{r.stores}</td><td>{r.versions}</td>
                        <td>{fmtDate(r.lastSubmittedAt)}</td><td>{r.submissionsWithAttention}</td><td>{r.attentionHits}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <TrendSection filters={applied} />

          <QuestionAnalyticsSection filters={applied} templates={templateRows} />

          <TextResponsesSection filters={applied} tzFor={tzFor} />
        </>
      )}
    </div>
  );
}

// --- Tarjetas resumen ---
function SummaryCards({ s }: { s: SupervisionSummary }) {
  const cards: Array<{ label: string; value: number; hint?: string; attention?: boolean }> = [
    { label: 'Enviados', value: s.submitted, hint: `${s.manual} manual · ${s.shiftClose} corte` },
    { label: 'Borradores en progreso', value: s.drafts, hint: 'Iniciados, sin enviar' },
    { label: 'Con atención', value: s.submissionsWithAttention, hint: `${s.attentionHits} hallazgos`, attention: s.submissionsWithAttention > 0 },
    { label: 'Requieren revision', value: s.pendingReview, attention: s.pendingReview > 0 },
    { label: 'Revisados', value: s.reviewed },
  ];
  return (
    <div className="form-supervision__cards">
      {cards.map((c) => (
        <div key={c.label} className={`stat-card ${c.attention ? 'stat-card--attention' : ''}`}>
          <span className="stat-card__value">{c.value}</span>
          <span className="stat-card__label">{c.label}</span>
          {c.hint && <span className="stat-card__hint">{c.hint}</span>}
        </div>
      ))}
    </div>
  );
}

// --- Bandeja de atencion ---
function AttentionInbox({ filters, canReview, tzFor, onReviewed }: { filters: SupervisionFilters; canReview: boolean; tzFor: (id: string) => string; onReviewed: () => void }) {
  const tier = useShellTier();
  const [reviewStatus, setReviewStatus] = useState<ReviewStatus>('PENDING');
  const [items, setItems] = useState<AttentionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [busyKey, setBusyKey] = useState<string>();
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true); setError(undefined);
    try {
      const res = await getAttentionInbox(filters, { reviewStatus, limit, offset });
      setItems(res.items); setTotal(res.pagination.total);
    } catch (e) { setError(getErrorMessage(e, 'No fue posible cargar la bandeja de atención.')); }
    finally { setLoading(false); }
  }, [filters, reviewStatus, offset]);

  useEffect(() => { setOffset(0); }, [filters, reviewStatus]);
  useEffect(() => { void load(); }, [load]);

  const markReviewed = async (it: AttentionItem) => {
    const key = `${it.submissionId}:${it.questionId}`;
    setBusyKey(key); setError(undefined);
    try { await reviewAttention(it.submissionId, it.questionId); await load(); onReviewed(); }
    catch (e) { setError(getErrorMessage(e, 'No fue posible marcar como revisado.')); }
    finally { setBusyKey(undefined); }
  };

  return (
    <section className="form-supervision__section" aria-label="Bandeja de atención">
      <div className="form-supervision__section-head">
        <h3>Bandeja de atencion</h3>
        <label className="field field--inline"><span>Estado</span>
          <select value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value as ReviewStatus)}>
            <option value="PENDING">Pendientes</option>
            <option value="REVIEWED">Revisados</option>
            <option value="ALL">Todos</option>
          </select>
        </label>
      </div>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {loading ? <div className="inline-loading"><span className="spinner" />Cargando…</div> : items.length === 0 ? (
        <p className="form-supervision__empty">Sin hallazgos {reviewStatus === 'PENDING' ? 'pendientes' : reviewStatus === 'REVIEWED' ? 'revisados' : ''} en el periodo.</p>
      ) : tier === 'compact' ? (
        // P-076: la bandeja es una lista operativa (pregunta, respuesta, quién, estado, revisar): en el teléfono, filas
        // compactas con la respuesta a la vista; la tabla sigue en tableta y escritorio.
        <DenseList label="Hallazgos por revisar" separated>
          {items.map((it) => {
            const key = `${it.submissionId}:${it.questionId}`;
            return (
              <DenseRow
                actions={!it.reviewed && canReview ? <Button disabled={busyKey === key} onClick={() => void markReviewed(it)} variant="ghost">Marcar revisado</Button> : undefined}
                badge={it.reviewed
                  ? <span className="form-badge form-badge--active" title={`${it.reviewedByName ?? ''} · ${fmtDateTime(it.reviewedAt, tzFor(it.storeId))}`}>Revisado</span>
                  : <span className="form-badge form-badge--attention">Pendiente</span>}
                flagged={!it.reviewed}
                key={key}
                meta={[
                  /* Codex R2 · M3: la respuesta puede ser un párrafo; en la línea de datos se parte (en «figures» iba en una sola línea). */
                  `Respuesta: ${it.answer}`,
                  fmtDateTime(it.submittedAt, tzFor(it.storeId)),
                  it.respondentName,
                  `${contextLabel(it.context)}${it.context === 'SHIFT_CLOSE' && it.shiftCloseVoided ? ' · corte anulado' : ''}`,
                  ...(it.reviewed && it.reviewedByName ? [`Revisó: ${it.reviewedByName}`] : []),
                ]}
                name={it.questionText}
                subtitle={`${it.formName ?? '—'} · ${it.storeName}`}
                title={it.questionText}
              />
            );
          })}
        </DenseList>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead><tr><th>Fecha</th><th>Tienda</th><th>Formulario</th><th>Pregunta</th><th>Respuesta</th><th>Quien</th><th>Origen</th><th>Estado</th><th>Acción</th></tr></thead>
            <tbody>
              {items.map((it) => {
                const key = `${it.submissionId}:${it.questionId}`;
                return (
                  <tr key={key} className={it.reviewed ? '' : 'data-row--attention'}>
                    <td>{fmtDateTime(it.submittedAt, tzFor(it.storeId))}</td>
                    <td>{it.storeName}</td>
                    <td>{it.formName ?? '—'}</td>
                    <td>{it.questionText}</td>
                    <td><strong>{it.answer}</strong></td>
                    <td>{it.respondentName}</td>
                    <td>
                      {contextLabel(it.context)}
                      {it.context === 'SHIFT_CLOSE' && it.shiftCloseVoided && <span className="form-badge form-badge--inactive" title="El corte fue anulado"> corte anulado</span>}
                    </td>
                    <td>
                      {it.reviewed
                        ? <span className="form-badge form-badge--active" title={`${it.reviewedByName ?? ''} · ${fmtDateTime(it.reviewedAt, tzFor(it.storeId))}`}>Revisado</span>
                        : <span className="form-badge form-badge--attention">Pendiente</span>}
                    </td>
                    <td>
                      {!it.reviewed && canReview && (
                        <button className="text-button" disabled={busyKey === key} onClick={() => void markReviewed(it)} type="button">Marcar revisado</button>
                      )}
                      {it.reviewed && it.reviewedByName && <small>{it.reviewedByName}</small>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {total > limit && (
        <div className="form-supervision__pager">
          <button className="text-button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} type="button">← Anteriores</button>
          <span>{offset + 1}–{Math.min(offset + limit, total)} de {total}</span>
          <button className="text-button" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} type="button">Siguientes →</button>
        </div>
      )}
    </section>
  );
}

// --- Tendencia ---
function TrendSection({ filters }: { filters: SupervisionFilters }) {
  const [bucket, setBucket] = useState<TrendBucket>('DAY');
  const [points, setPoints] = useState<TrendPoint[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getSupervisionTrends(filters, { bucket })
      .then((res) => { if (alive) setPoints(res.points); })
      .catch(() => { if (alive) setPoints([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [filters, bucket]);

  const max = Math.max(1, ...points.map((p) => p.submissions));
  // El bucket ya viene truncado EN TZ DE TIENDA por el SQL (naive → serializado como UTC): se
  // formatea en UTC para no correr el día al mostrarse desde otra zona horaria del dispositivo.
  const bucketFmt = (iso: string) => (bucket === 'MONTH' ? new Date(iso).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', timeZone: 'UTC' }) : fmtDate(iso, 'UTC'));

  return (
    <section className="form-supervision__section" aria-label="Tendencia">
      <div className="form-supervision__section-head">
        <h3>Tendencia</h3>
        <label className="field field--inline"><span>Agrupar por</span>
          <select value={bucket} onChange={(e) => setBucket(e.target.value as TrendBucket)}>
            <option value="DAY">Dia</option><option value="WEEK">Semana</option><option value="MONTH">Mes</option>
          </select>
        </label>
      </div>
      {loading ? <div className="inline-loading"><span className="spinner" />Cargando…</div> : points.length === 0 ? (
        <p className="form-supervision__empty">Sin datos en el periodo.</p>
      ) : (
        // Barras CSS puras (sin dependencias de graficos). Cada punto: enviados (barra) + hallazgos.
        <div className="form-trend" role="img" aria-label="Grafica de envios por periodo">
          {points.map((p) => (
            <div key={p.bucket} className="form-trend__col" title={`${bucketFmt(p.bucket)}: ${p.submissions} enviados, ${p.attentionHits} hallazgos`}>
              <div className="form-trend__bar" style={{ height: `${Math.round((p.submissions / max) * 100)}%` }}>
                {p.attentionHits > 0 && <div className="form-trend__bar-attention" style={{ height: `${Math.round((Math.min(p.attentionHits, p.submissions) / Math.max(1, p.submissions)) * 100)}%` }} />}
              </div>
              <span className="form-trend__label">{bucketFmt(p.bucket)}</span>
              <span className="form-trend__value">{p.submissions}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// --- Analitica por pregunta ---
function QuestionAnalyticsSection({ filters, templates }: { filters: SupervisionFilters; templates: SupervisionTemplateRow[] }) {
  // Requiere un formulario elegido (contrato backend). Si el filtro global ya fija uno, lo usamos.
  const [tid, setTid] = useState(filters.templateId ?? '');
  const [questions, setQuestions] = useState<TemplateQuestionOption[]>([]);
  const [logicalKey, setLogicalKey] = useState('');
  const [analytics, setAnalytics] = useState<QuestionAnalytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => { setTid(filters.templateId ?? ''); }, [filters.templateId]);

  useEffect(() => {
    if (!tid) { setQuestions([]); setLogicalKey(''); setAnalytics(null); return; }
    let alive = true;
    getTemplateQuestions({ ...filters, templateId: tid })
      .then((res) => { if (alive) { setQuestions(res.questions); setLogicalKey(''); setAnalytics(null); } })
      .catch(() => { if (alive) setQuestions([]); });
    return () => { alive = false; };
  }, [tid, filters]);

  useEffect(() => {
    if (!tid || !logicalKey) { setAnalytics(null); return; }
    let alive = true;
    setLoading(true); setError(undefined);
    getQuestionAnalytics(logicalKey, { ...filters, templateId: tid })
      .then((res) => { if (alive) setAnalytics(res); })
      .catch((e) => { if (alive) setError(getErrorMessage(e, 'No fue posible cargar la analitica de la pregunta.')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tid, logicalKey, filters]);

  return (
    <section className="form-supervision__section" aria-label="Analitica por pregunta">
      <h3>Analitica por pregunta</h3>
      <div className="form-supervision__qpickers">
        <label className="field field--inline"><span>Formulario</span>
          <select value={tid} onChange={(e) => setTid(e.target.value)}>
            <option value="">Elige un formulario</option>
            {templates.map((t) => <option key={t.templateId} value={t.templateId}>{t.name ?? '(sin nombre)'}</option>)}
          </select>
        </label>
        <label className="field field--inline"><span>Pregunta</span>
          <select value={logicalKey} disabled={!tid || questions.length === 0} onChange={(e) => setLogicalKey(e.target.value)}>
            <option value="">{tid ? (questions.length ? 'Elige una pregunta' : 'Sin respuestas') : 'Elige un formulario primero'}</option>
            {questions.map((q) => <option key={q.logicalKey} value={q.logicalKey}>{q.text} ({QUESTION_TYPE_LABELS[q.type]})</option>)}
          </select>
        </label>
      </div>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {loading && <div className="inline-loading"><span className="spinner" />Cargando…</div>}
      {analytics && !loading && <QuestionSeriesView analytics={analytics} />}
    </section>
  );
}

function QuestionSeriesView({ analytics }: { analytics: QuestionAnalytics }) {
  if (analytics.series.length === 0) return <p className="form-supervision__empty">Sin respuestas para esta pregunta en el periodo.</p>;
  return (
    <div className="form-qanalytics">
      {analytics.note && <p className="form-supervision__note">{analytics.note}</p>}
      {analytics.series.map((s, i) => (
        <div key={i} className="form-qanalytics__series">
          <div className="form-qanalytics__head"><strong>{s.text}</strong> <span className="form-badge">{QUESTION_TYPE_LABELS[s.type]}</span>{s.versions > 1 && <small> · {s.versions} versiones</small>}</div>
          {s.yesNo && (
            <div className="form-qanalytics__grid">
              <span>Respondidas: <strong>{s.yesNo.total}</strong></span>
              <span>Si: <strong>{s.yesNo.yes}</strong> ({pct(s.yesNo.yes, s.yesNo.total)})</span>
              <span>No: <strong>{s.yesNo.no}</strong> ({pct(s.yesNo.no, s.yesNo.total)})</span>
              <span>Hallazgos: <strong>{s.yesNo.attentionHits}</strong></span>
            </div>
          )}
          {s.number && (
            <div className="form-qanalytics__grid">
              <span>Respondidas: <strong>{s.number.count}</strong></span>
              <span>Suma: <strong>{s.number.sum}</strong></span>
              <span>Promedio: <strong>{s.number.average ?? '—'}</strong></span>
              <span>Min: <strong>{s.number.min ?? '—'}</strong></span>
              <span>Max: <strong>{s.number.max ?? '—'}</strong></span>
              <span>Hallazgos: <strong>{s.number.attentionHits}</strong></span>
            </div>
          )}
          {s.distribution && (
            <div className="data-table-wrap">
              <table className="data-table n3-table--analytic">
                <thead><tr><th>Opcion</th><th>Selecciones</th><th>Envios</th><th>% de envios</th></tr></thead>
                <tbody>
                  {s.distribution.options.map((o) => (
                    <tr key={o.optionLogicalKey}>
                      <td>{o.label}</td><td>{o.selections}</td><td>{o.submissions}</td>
                      <td>{pct(o.submissions, s.distribution!.answeredSubmissions)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <small className="form-supervision__note">% calculado sobre {s.distribution.answeredSubmissions} envios que respondieron esta pregunta.</small>
            </div>
          )}
          {!s.yesNo && !s.number && !s.distribution && <p className="form-supervision__empty">Este tipo de pregunta se consulta en “Respuestas de texto”.</p>}
        </div>
      ))}
    </div>
  );
}

// --- Respuestas de texto recientes ---
function TextResponsesSection({ filters, tzFor }: { filters: SupervisionFilters; tzFor: (id: string) => string }) {
  const tier = useShellTier();
  const [items, setItems] = useState<TextItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const limit = 25;

  useEffect(() => { setOffset(0); }, [filters]);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    getTextResponses(filters, { limit, offset })
      .then((res) => { if (alive) { setItems(res.items); setTotal(res.pagination.total); } })
      .catch(() => { if (alive) { setItems([]); setTotal(0); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [filters, offset]);

  return (
    <section className="form-supervision__section" aria-label="Respuestas de texto recientes">
      <h3>Respuestas de texto recientes</h3>
      {loading ? <div className="inline-loading"><span className="spinner" />Cargando…</div> : items.length === 0 ? (
        <p className="form-supervision__empty">Sin respuestas de texto en el periodo.</p>
      ) : tier === 'compact' ? (
        // P-076: respuestas de texto como filas compactas (el texto necesita ancho): respuesta, pregunta, quién y cuándo.
        <DenseList label="Respuestas de texto recientes">
          {items.map((it) => (
            <DenseRow
              badge={it.attention ? <span className="form-badge form-badge--attention">atencion</span> : undefined}
              flagged={it.attention}
              key={`${it.submissionId}:${it.questionId}`}
              meta={[fmtDateTime(it.submittedAt, tzFor(it.storeId)), it.storeName, it.formName ?? '—', it.respondentName]}
              subtitle={it.questionText}
              title={it.text}
            />
          ))}
        </DenseList>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead><tr><th>Fecha</th><th>Tienda</th><th>Formulario</th><th>Pregunta</th><th>Respuesta</th><th>Quien</th></tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={`${it.submissionId}:${it.questionId}`} className={it.attention ? 'data-row--attention' : ''}>
                  <td>{fmtDateTime(it.submittedAt, tzFor(it.storeId))}</td><td>{it.storeName}</td><td>{it.formName ?? '—'}</td>
                  <td>{it.questionText}</td>
                  <td>{it.text}{it.attention && <span className="form-badge form-badge--attention"> atencion</span>}</td>
                  <td>{it.respondentName}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {total > limit && (
        <div className="form-supervision__pager">
          <button className="text-button" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))} type="button">← Anteriores</button>
          <span>{offset + 1}–{Math.min(offset + limit, total)} de {total}</span>
          <button className="text-button" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)} type="button">Siguientes →</button>
        </div>
      )}
    </section>
  );
}
